"use client";

import { useEffect } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import {
  changePlan,
  createCheckoutSession,
  createPortalSession,
  fetchBillingPlans,
  fetchChangePlanPreview,
  fetchOrgSubscription,
} from "@/lib/billing-api-client";
import { clearSubscriptionLocked, markSubscriptionLocked } from "@/lib/billing-lock";
import { billingKeys } from "@/lib/query/billing-keys";
import { queryKeys } from "@/lib/query/keys";
import type { ChangePlanRequest, SubscriptionView } from "@/types/billing";

/**
 * What an org without a subscription row on a billing-less install looks like:
 * no limits, nothing to enforce. Also the shape used while the query has not
 * answered yet, so limit checks never fire on a loading screen.
 */
export const UNLIMITED_SUBSCRIPTION: SubscriptionView = {
  state: "unlimited",
  limits: { maxNamespaces: null, maxSeats: null },
  usage: { namespaces: 0, seats: 0 },
};

export function useBillingPlans(options?: { enabled?: boolean }) {
  return useQuery({
    queryKey: billingKeys.plans(),
    queryFn: fetchBillingPlans,
    enabled: options?.enabled ?? true,
    staleTime: 5 * 60_000,
  });
}

/**
 * The caller's org subscription. A 404 (billing disabled) resolves to the
 * `unlimited` view and is never an error. `subscription` is always defined;
 * read `isPending` when the distinction between "unlimited" and "not loaded"
 * matters.
 */
export function useOrgSubscription(options?: { enabled?: boolean; refetchInterval?: number | false }) {
  const query = useQuery({
    queryKey: billingKeys.subscription(),
    queryFn: async () => (await fetchOrgSubscription()) ?? UNLIMITED_SUBSCRIPTION,
    enabled: options?.enabled ?? true,
    refetchInterval: options?.refetchInterval ?? false,
    staleTime: 30_000,
  });

  const state = query.data?.state;
  // Keep the process-wide lock flag in step with what the server says, so a
  // 402 from any namespace query and the subscription itself agree.
  useEffect(() => {
    if (state === "locked") {
      markSubscriptionLocked();
    } else if (state !== undefined) {
      clearSubscriptionLocked();
    }
  }, [state]);

  return {
    ...query,
    subscription: query.data ?? UNLIMITED_SUBSCRIPTION,
  };
}

export function useInvalidateSubscription() {
  const queryClient = useQueryClient();
  return () => queryClient.invalidateQueries({ queryKey: billingKeys.subscription() });
}

/** POST /api/billing/checkout → the hosted Checkout URL. The caller navigates there. */
export function useCheckoutMutation() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (planSlug: string) => createCheckoutSession(planSlug),
    onSuccess: () => {
      // The server wrote a `pending` row; a later refetch should see it.
      queryClient.invalidateQueries({ queryKey: billingKeys.subscription() });
    },
  });
}

/** POST /api/billing/portal → the Customer Portal URL. The caller navigates there. */
export function usePortalMutation() {
  return useMutation({
    mutationFn: () => createPortalSession(),
  });
}

/** GET /api/billing/change-plan/preview?planSlug= — only runs once a plan is picked. */
export function useChangePlanPreview(planSlug: string | null) {
  return useQuery({
    queryKey: billingKeys.changePlanPreview(planSlug ?? ""),
    queryFn: () => fetchChangePlanPreview(planSlug as string),
    enabled: Boolean(planSlug),
    staleTime: 0,
  });
}

export function useChangePlanMutation() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (payload: ChangePlanRequest) => changePlan(payload),
    onSuccess: (next) => {
      queryClient.setQueryData(billingKeys.subscription(), next);
      // A downgrade may have paused namespaces or removed members.
      queryClient.invalidateQueries({ queryKey: queryKeys.namespaces() });
      queryClient.invalidateQueries({ queryKey: ["adminUsers"] });
      queryClient.invalidateQueries({ queryKey: ["adminInvites"] });
    },
  });
}
